import { Configuration, LogLevel } from '@azure/msal-browser';

/**
 * Microsoft Entra (Azure AD) Configuration
 * 
 * Required environment variables:
 * - NEXT_PUBLIC_AZURE_AD_CLIENT_ID: Application (client) ID from Azure AD app registration
 * - NEXT_PUBLIC_AZURE_AD_TENANT_ID: Directory (tenant) ID from Azure AD
 * - NEXT_PUBLIC_AZURE_AD_REDIRECT_URI: Redirect URI configured in Azure AD app registration (defaults to /auth/callback)
 */

/**
 * MSAL Configuration object
 * @see https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: process.env.NEXT_PUBLIC_AZURE_AD_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${process.env.NEXT_PUBLIC_AZURE_AD_TENANT_ID || 'common'}`,
    redirectUri: process.env.NEXT_PUBLIC_AZURE_AD_REDIRECT_URI || 
                 (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '/auth/callback'),
    postLogoutRedirectUri: typeof window !== 'undefined'
      ? `${window.location.origin}/logout`
      : '/logout',
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage', // Store tokens in localStorage for persistence across sessions
    storeAuthStateInCookie: false, // Set to true if you have issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel: LogLevel.Warning,
    },
  },
};

/**
 * Scopes to request during login
 * - openid: Required for OpenID Connect authentication
 * - profile: Access to user's profile information
 * - email: Access to user's email address
 * - User.Read: Microsoft Graph API permission to read user profile
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Scopes to request for Microsoft Graph API calls
 */
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  scopes: ['User.Read'],
};

/**
 * Protected resource map for token acquisition
 * Maps API endpoints to required scopes
 */
export const protectedResources = {
  graphMe: {
    endpoint: 'https://graph.microsoft.com/v1.0/me',
    scopes: ['User.Read'],
  },
  backendApi: {
    endpoint: process.env.NEXT_PUBLIC_VITE_BACKEND_URL || '',
    scopes: [process.env.NEXT_PUBLIC_API_TOKEN_SCOPE || ''],
  },
};
